import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAssetContext } from '@/context/AssetContext';
import { usePageContent, getSection } from '@/hooks/usePageContent';
import SEO from '@/components/SEO';
import { 
  Users, 
  Target, 
  Eye,
  Award,
  MapPin,
  Phone,
  Mail,
  CheckCircle2,
  ArrowRight,
  Heart,
  Globe,
  GraduationCap,
  Trophy,
  Linkedin,
  Building,
  Calendar,
  Sparkles,
  Star,
  Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API_URL = window.location.origin;

// Fallback leadership data (used if API fails)
const fallbackLeadership = [
  {
    name: 'Harvinder Singh',
    role: 'Founder & CEO',
    department: 'Leadership',
    image_url: '/images/team/Harvinder BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Inthiyaz',
    role: 'Co-Founder & COO',
    department: 'Leadership',
    image_url: '/images/team/Inthiyaz BW.jpg',
    linkedin_url: '#',
  },
];

// Full team data with local images
const fullTeamData = [
  // Leadership
  {
    name: 'Harvinder Singh',
    role: 'Founder & CEO',
    department: 'Leadership',
    image_url: '/images/team/Harvinder BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Inthiyaz',
    role: 'Co-Founder & COO',
    department: 'Leadership',
    image_url: '/images/team/Inthiyaz BW.jpg',
    linkedin_url: '#',
  },
  // Senior Team
  {
    name: 'Sandip Mohan',
    role: 'Head of Operations',
    department: 'Operations',
    image_url: '/images/team/Sandip Mohan BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Jayaprabha',
    role: 'Senior Counselor',
    department: 'Counseling',
    image_url: '/images/team/Jayaprabha BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Eduri Sharon',
    role: 'Regional Head',
    department: 'Regional',
    image_url: '/images/team/Eduri Sharon BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Saeeda Khasim',
    role: 'Senior Counselor',
    department: 'Counseling',
    image_url: '/images/team/Saeeda Khasim BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Pandiyan',
    role: 'Branch Manager',
    department: 'Regional',
    image_url: '/images/team/Pandiyan BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Shaina',
    role: 'Marketing Head',
    department: 'Marketing',
    image_url: '/images/team/Shaina BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Radika',
    role: 'Senior Counselor',
    department: 'Counseling',
    image_url: '/images/team/Radika BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Uma',
    role: 'Counselor',
    department: 'Counseling',
    image_url: '/images/team/Uma BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Revathi Punjala',
    role: 'Counselor',
    department: 'Counseling',
    image_url: '/images/team/Revathi Punjala BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Pooja Motwani',
    role: 'Counselor',
    department: 'Counseling',
    image_url: '/images/team/Pooja Motwani.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Anitha',
    role: 'Counselor',
    department: 'Counseling',
    image_url: '/images/team/Anitha BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Mamta',
    role: 'Counselor',
    department: 'Counseling',
    image_url: '/images/team/Mamta BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Anupama',
    role: 'Counselor',
    department: 'Counseling',
    image_url: '/images/team/Anupama BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Vineetha',
    role: 'Counselor',
    department: 'Counseling',
    image_url: '/images/team/Vineetha BW.jpg',
    linkedin_url: '#',
  },
  {
    name: 'Apala',
    role: 'Counselor',
    department: 'Counseling',
    image_url: '/images/team/Apala.jpg',
    linkedin_url: '#',
  },
];

// Organization Timeline - Accurate company history
const timeline = [
  {
    date: '17th Oct 2020',
    year: '2020',
    title: 'Inauguration of UniXperts',
    description: 'Started with 18 Team Members',
    icon: Sparkles,
    highlight: 'Founded'
  },
  {
    date: 'Dec 2020',
    year: '2020',
    title: '1st Office Vijayawada',
    description: 'First branch office opened in Andhra Pradesh',
    icon: Building,
    highlight: '1st Office'
  },
  {
    date: 'Dec 2020',
    year: '2020',
    title: '2nd Office Hyderabad',
    description: 'Expanded to Telangana capital',
    icon: Building,
    highlight: '2nd Office'
  },
  {
    date: 'Jan 2021',
    year: '2021',
    title: '3rd Office Kochi',
    description: 'Entered Kerala market',
    icon: Building,
    highlight: '3rd Office'
  },
  {
    date: 'Sep 2021',
    year: '2021',
    title: '4th Office Vizag',
    description: 'Strengthened presence in coastal Andhra',
    icon: Building,
    highlight: '4th Office'
  },
  {
    date: 'Mar 2022',
    year: '2022',
    title: '5th Office Ahmedabad',
    description: 'First office in Gujarat',
    icon: Building,
    highlight: '5th Office'
  },
  {
    date: 'May 2022',
    year: '2022',
    title: '6th Office Kottayam',
    description: 'Deepened Kerala presence',
    icon: Building,
    highlight: '6th Office'
  },
  {
    date: 'Aug 2022',
    year: '2022',
    title: '7th Office Gurgaon',
    description: 'Established Head Office in NCR',
    icon: Star,
    highlight: 'Head Office'
  },
  {
    date: 'May 2023',
    year: '2023',
    title: '8th Office Kukatpally',
    description: 'Second office in Hyderabad region',
    icon: Building,
    highlight: '8th Office'
  },
  {
    date: 'Mar 2024',
    year: '2024',
    title: '9th Office Chennai',
    description: 'Entered Tamil Nadu market',
    icon: Building,
    highlight: '9th Office'
  },
  {
    date: 'Apr 2024',
    year: '2024',
    title: '10th Office Pune',
    description: 'Expanded to Maharashtra',
    icon: Building,
    highlight: '10th Office'
  },
  {
    date: 'Jul 2025',
    year: '2025',
    title: '11th Office Bengaluru',
    description: 'Latest expansion to tech capital',
    icon: Award,
    highlight: '11th Office'
  },
];

// Life at UniXperts - Photo gallery (uses dynamic photos from AssetContext)
const lifeAtUnixpertsConfig = [
  { key: 'life_photo_01', title: 'Team Celebrations', description: 'Celebrating milestones together' },
  { key: 'life_photo_02', title: 'Office Celebrations', description: 'Building memories at work' },
  { key: 'life_photo_03', title: 'Team Building', description: 'Growing stronger together' },
  { key: 'life_photo_04', title: 'Festival Vibes', description: 'Diwali celebrations at office' },
  { key: 'life_photo_05', title: 'Team Outings', description: 'Fun times outside work' },
  { key: 'life_photo_06', title: 'Office Culture', description: 'Modern and vibrant workspace' },
  { key: 'life_photo_07', title: 'Learning Sessions', description: 'Continuous growth and development' },
  { key: 'life_photo_08', title: 'Team Bonding', description: 'Creating lasting connections' },
  { key: 'life_photo_09', title: 'Student Success', description: 'Every visa approval is a celebration' },
  { key: 'life_photo_10', title: 'Work Anniversaries', description: 'Honoring our journey together' },
  { key: 'life_photo_11', title: 'Award Ceremonies', description: 'Recognizing excellence' },
  { key: 'life_photo_12', title: 'Team Retreat', description: 'Recharging and reconnecting' },
  { key: 'life_photo_13', title: 'Fun at Work', description: 'Work hard, play harder' },
  { key: 'life_photo_14', title: 'Office Vibes', description: 'Where magic happens daily' },
];

const achievements = [
  { number: '30,000+', label: 'Students Placed', icon: Users },
  { number: '15+', label: 'Yrs Avg Sector Exp', icon: Trophy },
  { number: '500+', label: 'Partner Universities', icon: GraduationCap },
  { number: '15', label: 'Countries', icon: Globe },
  { number: '98%', label: 'Visa Success', icon: Star },
  { number: 'Free', label: 'End-to-End Guidance', icon: CheckCircle2 },
];

const values = [
  {
    icon: Target,
    title: 'Student-First Approach',
    description: 'Every decision we make is centered around what\'s best for our students\' future.',
    color: 'cyan'
  },
  {
    icon: Award,
    title: 'Excellence in Guidance',
    description: 'We maintain the highest standards of counseling and support services.',
    color: 'violet'
  },
  {
    icon: Heart,
    title: 'Personalized Support',
    description: 'No two students are alike. We provide customized guidance for each individual.',
    color: 'coral'
  },
  {
    icon: Eye,
    title: 'Transparency',
    description: 'Clear communication, honest advice, and no hidden fees or surprises.',
    color: 'green'
  },
];

const offices = [
  {
    city: 'Gurugram (Head Office)',
    address: '1st Floor, Plot No. 114, Sector 44, Gurugram, Haryana 122003',
    phone: '+91 96395 57799',
    email: 'info@unixperts.com',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600'
  },
];

export const AboutPage = () => {
  const { isDark } = useTheme();
  const { getLogo, getGPTWBadge, getAsset } = useAssetContext();
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(true);
  const [lifePhotos, setLifePhotos] = useState([]);
  
  // Fetch CMS content for the About page
  const { content: cmsContent } = usePageContent('about');
  
  // Get CMS-managed content with fallbacks to hardcoded values
  const heroContent = getSection(cmsContent, 'hero', {
    badge_text: 'About UniXperts',
    title: 'Your Trusted Partner in',
    title_highlight: 'Global Education',
    subtitle: 'Backed by a team with 15+ years of collective study-abroad experience, UniXperts has been helping students achieve their dreams of studying abroad since 2020. We combine expert guidance with cutting-edge technology to provide personalized support at every step.'
  });
  
  const storyContent = getSection(cmsContent, 'story', {
    badge: 'Our Story',
    title: 'Know Our Origin - The Big',
    title_highlight: 'WHY',
    paragraphs: [
      'Indian study abroad dreams have always been huge. Beyond the personal and career benefits that studying abroad promises, it is a strong statement that students want to make - the statement that they dare to dream and live big.',
      'To help students of this generation realize their extraordinary dreams, it takes an equally smart approach to study abroad guidance. A smart and honest guidance that keeps students\' best interests ahead of everything. This unfilled need in the Indian study-abroad consulting space gave birth to UniXperts.',
      'At UniXperts, students\' success is paramount. Our experienced team invests sufficient time in each student, building transparent processes from end to end to ensure they receive quality advice and achieve their dreams the smart and right way.'
    ]
  });
  
  // Get CMS-managed stats, timeline, values, offices with fallbacks to hardcoded arrays
  const cmsStats = getSection(cmsContent, 'stats', null);
  const statsContent = cmsStats && Array.isArray(cmsStats) && cmsStats.length > 0 ? cmsStats : achievements;
  
  const cmsTimeline = getSection(cmsContent, 'timeline', null);
  const timelineContent = cmsTimeline && Array.isArray(cmsTimeline) && cmsTimeline.length > 0 ? cmsTimeline : timeline;
  
  const cmsValues = getSection(cmsContent, 'values', null);
  const valuesContent = cmsValues && Array.isArray(cmsValues) && cmsValues.length > 0 ? cmsValues : values;
  
  const cmsOffice = getSection(cmsContent, 'office', null);
  const officesContent = cmsOffice && (Array.isArray(cmsOffice) ? cmsOffice.length > 0 : cmsOffice.name) ? 
    (Array.isArray(cmsOffice) ? cmsOffice : [cmsOffice]) : offices;

  // Section headers + bannerss (Phase C wiring)
  const leadershipHeader = getSection(cmsContent, 'leadership_header', {
    badge: 'Leadership Team',
    title: 'The Minds Shaping',
    title_highlight: 'Our Future',
    subtitle: 'Every study abroad journey deserves a team that listens, understands, and guides with care. Ours brings together experts who specialize in country-specific admissions, visa processes, and long-term academic planning.'
  });
  const valuesHeader = getSection(cmsContent, 'values_header', {
    badge: 'Our Values',
    title: 'What We',
    title_highlight: 'Stand For'
  });
  const cultureHeader = getSection(cmsContent, 'culture_header', {
    badge: 'Our Culture',
    title: 'Life @',
    title_highlight: 'UniXperts',
    subtitle: 'Day in, day out we do the most impactful work on the student community of India but we are as fun as we are serious about our work. Spread across 11 offices in India, our team is as diverse as it can get.'
  });
  const gptwBanner = getSection(cmsContent, 'gptw_banner', {
    title: 'Certified Great Place to Work',
    subtitle: 'Recognized for our exceptional workplace culture - September 2025 to September 2026',
    cta_text: 'Join Our Team',
    cta_link: 'mailto:hr@unixperts.com'
  });
  const careersCta = getSection(cmsContent, 'careers_cta', {
    title: 'Want to Make a Difference?',
    subtitle: "If you're looking for a place where you can work with students, make a real impact in their lives, feel valued for your contributions, and celebrate success with a team, we should talk.",
    cta_primary_text: 'hr@unixperts.com',
    cta_primary_link: 'mailto:hr@unixperts.com'
  });
  
  const seoContent = getSection(cmsContent, 'seo', {
    title: 'About Us',
    description: 'Learn about UniXperts - India\'s trusted study abroad consultancy. Great Place to Work certified, with 15+ years of experience, we\'ve helped 30,000+ students achieve their dream of studying in top universities worldwide.',
    keywords: 'about unixperts, study abroad consultants, education consultancy India, international education experts, great place to work'
  });
  
  // Fetch team members from API
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await fetch(`${API_URL}/api/team/?active_only=true`);
        const data = await res.json();
        if (data.team_members && data.team_members.length > 0) {
          setTeamMembers(data.team_members);
        } else {
          // Use full team data as fallback
          setTeamMembers(fullTeamData);
        }
      } catch (error) {
        console.error('Error fetching team:', error);
        // Use full team data as fallback
        setTeamMembers(fullTeamData);
      } finally {
        setTeamLoading(false);
      }
    };
    fetchTeam();
    
    // Fetch life photos from Page Content CMS
    const fetchLifePhotos = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/pages/public/about`);
        if (res.ok) {
          const data = await res.json();
          if (data.content && data.content.life_photos && Array.isArray(data.content.life_photos)) {
            // Filter out empty entries and map to proper format
            const photos = data.content.life_photos
              .filter(photo => {
                const url = typeof photo === 'string' ? photo : (photo?.url || photo?.image);
                return url && url.trim() !== '';
              })
              .map((photo, idx) => ({
                key: `cms_life_photo_${idx}`,
                title: typeof photo === 'object' ? (photo.alt || photo.caption || `Photo ${idx + 1}`) : `Photo ${idx + 1}`,
                description: typeof photo === 'object' ? (photo.description || '') : '',
                image: typeof photo === 'string' ? photo : (photo?.url || photo?.image || '')
              }));
            
            if (photos.length > 0) {
              setLifePhotos(photos);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching life photos:', error);
      }
    };
    fetchLifePhotos();
  }, []);
  
  // Build life photos array - prioritize CMS content, fallback to AssetContext
  const lifeAtUnixperts = lifePhotos.length > 0 
    ? lifePhotos 
    : lifeAtUnixpertsConfig.map(item => ({
        ...item,
        image: getAsset(item.key)
      }));
  
  // Get appropriate GPTW badge based on context
  // Color badge for hero (white background container)
  // White badge for dark backgrounds, Black for light backgrounds
  const gptwBadgeHero = getGPTWBadge('color');
  const gptwBadgeDark = getGPTWBadge('white');
  const gptwBadgeLight = getGPTWBadge('black');
  
  const colorClasses = {
    cyan: 'from-ux-cyan to-ux-blue',
    violet: 'from-ux-violet to-ux-rose',
    coral: 'from-ux-coral to-ux-amber',
    green: 'from-ux-green to-ux-mint',
  };

  return (
    <>
      <SEO 
        title={seoContent.title}
        description={seoContent.description}
        keywords={seoContent.keywords}
        canonical="https://unixperts.com/about"
      />
      <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`} data-testid="about-page">
      
      {/* Hero Section with GPTW Badge */}
      <section className="py-20 md:py-32 relative overflow-hidden min-h-[65vh] flex items-center">
        <div className="absolute inset-0">
          <img 
            src={getAsset('hero_about') || "https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/a7d36080eefe4afae8fad83660b42f1f011c13f5796ff7c5777f49cb09ec47ba.png"}
            alt="UniXperts team advising students"
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-[#1a2830]/95 via-[#233842]/80 to-transparent' : 'bg-gradient-to-r from-ux-charcoal/95 via-ux-charcoal/80 to-ux-violet-dark/60'}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-3xl">
              <Badge className="bg-white/10 text-white border-white/20 mb-6 backdrop-blur-sm">{heroContent.badge_text}</Badge>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-white tracking-tight">
                {heroContent.title} <span className="gradient-text-aurora">{heroContent.title_highlight}</span>
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed">
                {heroContent.subtitle}
              </p>
            </div>
            
            {/* GPTW Badge - Aligned with logo */}
            <div className="flex-shrink-0">
              <div className="bg-white rounded-xl p-3 shadow-2xl">
                <img 
                  src={gptwBadgeHero}
                  alt="Great Place to Work Certified - Sep 2025 to Sep 2026"
                  className="h-32 md:h-40 w-auto object-contain"
                  data-testid="gptw-badge"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className={`py-16 ${isDark ? 'bg-[#233842]' : 'bg-ux-slate-light'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {(Array.isArray(statsContent) ? statsContent : achievements).map((stat, index) => {
              // Icon mapping for CMS-provided icons (string) or use component directly
              const iconMap = { 
                Users, Trophy, GraduationCap, Globe, Star, CheckCircle2,
                // Also support lowercase/hyphenated versions from CMS
                'users': Users, 
                'trophy': Trophy, 
                'graduation-cap': GraduationCap, 
                'graduationcap': GraduationCap,
                'globe': Globe, 
                'star': Star, 
                'check-circle': CheckCircle2,
                'checkcircle2': CheckCircle2,
                'check-circle-2': CheckCircle2
              };
              // Determine icon: use the component reference directly if it exists, else look up in map
              const IconComponent = stat.icon && (typeof stat.icon === 'function' || typeof stat.icon === 'object')
                ? stat.icon 
                : (typeof stat.icon === 'string' ? iconMap[stat.icon] || iconMap[stat.icon.toLowerCase()] : null) || Users;
              return (
              <div 
                key={index} 
                className={`text-center p-6 rounded-2xl glass-panel hover:scale-105 transition-transform`}
              >
                <IconComponent className={`w-6 h-6 mx-auto mb-3 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
                <div className={`text-2xl md:text-3xl font-bold mb-1 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  {stat.number || stat.value}
                </div>
                <div className={`text-xs md:text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                  {stat.label}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className={`py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className={`mb-6 ${isDark ? 'bg-ux-violet/20 text-ux-violet' : 'bg-ux-violet/10 text-ux-violet'}`}>
                {storyContent.badge}
              </Badge>
              <h2 className={`text-3xl md:text-5xl font-bold mb-6 tracking-tight ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                {storyContent.title}
                <span className="gradient-text-electric"> {storyContent.title_highlight}</span> of UniXperts
              </h2>
              <div className={`space-y-4 text-lg ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                {(storyContent.paragraphs || []).map((para, idx) => (
                  <p key={idx}>{para}</p>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Button 
                  className="bg-gradient-to-r from-ux-cyan to-ux-blue text-white rounded-full px-6"
                  asChild
                >
                  <Link to="/contact">
                    Start Your Journey <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/12e294ed-05c7-4cf7-8350-fb3286237892/images/738cbbb6773bada55704ff9baadbf8d7a5eaac2bb046691d9709be6965500147.png" 
                  alt="Education counselor guiding student"
                  className="w-full h-96 object-cover"
                />
                <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-t from-[#1a2830]/80 to-transparent' : 'bg-gradient-to-t from-black/50 to-transparent'}`} />
              </div>
              {/* Floating GPTW mini badge */}
              <div className={`absolute -bottom-6 -left-6 p-3 rounded-2xl glass-panel ${isDark ? 'bg-[#233842]/90' : 'bg-white/90'} shadow-xl`}>
                <img src={isDark ? gptwBadgeDark : gptwBadgeLight} alt="Great Place to Work" className="h-20 w-auto" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section - Roadmap/Progress Style */}
      <section className={`py-24 ${isDark ? 'bg-[#233842]' : 'bg-ux-slate-light'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className={`mb-4 ${isDark ? 'bg-ux-amber/20 text-ux-amber' : 'bg-ux-amber/10 text-ux-amber'}`}>
              Our Journey
            </Badge>
            <h2 className={`text-3xl md:text-5xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
              Journey of
              <span className="gradient-text-sunset"> UniXperts</span>
            </h2>
            <p className={`mt-4 text-lg max-w-2xl mx-auto ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
              From a vision to India's fastest-growing study abroad consultancy
            </p>
          </div>
          
          {/* Horizontal Roadmap */}
          <div className="relative">
            {/* Progress Track - Desktop */}
            <div className="hidden md:block relative mb-8">
              {/* Background track */}
              <div className={`absolute top-6 left-0 right-0 h-1 ${isDark ? 'bg-white/10' : 'bg-ux-charcoal/10'} rounded-full`} />
              
              {/* Filled progress gradient */}
              <div className="absolute top-6 left-0 h-1 bg-gradient-to-r from-ux-amber via-ux-cyan to-ux-green rounded-full" style={{ width: '100%' }} />
              
              {/* Year markers */}
              <div className="relative flex justify-between">
                {(() => {
                  const timelineData = Array.isArray(timelineContent) ? timelineContent : timeline;
                  const years = [...new Set(timelineData.map(t => t.year))];
                  return years.map((year, index) => {
                    const isFirst = index === 0;
                    const isLast = index === years.length - 1;
                    const yearEvents = timelineData.filter(t => t.year === year);
                    
                    return (
                      <div key={year} className="flex flex-col items-center group cursor-pointer" style={{ flex: 1 }}>
                        <span className={`text-sm font-bold mb-2 ${
                          isFirst ? 'text-ux-amber' : isLast ? 'text-ux-green' : isDark ? 'text-white' : 'text-ux-charcoal'
                        }`}>
                          {year}
                        </span>
                        <div className={`relative w-5 h-5 rounded-full transition-all duration-300 group-hover:scale-125 ${
                          isFirst 
                            ? 'bg-gradient-to-br from-ux-amber to-ux-coral shadow-lg shadow-ux-amber/30' 
                            : isLast 
                            ? 'bg-gradient-to-br from-ux-green to-ux-cyan shadow-lg shadow-ux-green/30'
                            : 'bg-gradient-to-br from-ux-cyan to-ux-blue shadow-lg shadow-ux-cyan/30'
                        }`}>
                          <div className="absolute inset-1 rounded-full bg-white/30" />
                        </div>
                        <span className={`mt-2 text-xs px-2 py-0.5 rounded-full ${
                          isDark ? 'bg-white/10 text-white/60' : 'bg-ux-charcoal/10 text-ux-charcoal/60'
                        }`}>
                          {yearEvents.length} {yearEvents.length === 1 ? 'milestone' : 'milestones'}
                        </span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
            
            {/* Mobile: Year chips */}
            <div className="md:hidden flex justify-center gap-2 mb-8 flex-wrap">
              {(() => {
                const timelineData = Array.isArray(timelineContent) ? timelineContent : timeline;
                const years = [...new Set(timelineData.map(t => t.year))];
                return years.map((year, index) => (
                  <span key={year} className={`px-3 py-1 rounded-full text-sm font-medium ${
                    index === 0 
                      ? 'bg-ux-amber/20 text-ux-amber' 
                      : index === years.length - 1 
                      ? 'bg-ux-green/20 text-ux-green'
                      : isDark ? 'bg-white/10 text-white' : 'bg-ux-charcoal/10 text-ux-charcoal'
                  }`}>
                    {year}
                  </span>
                ));
              })()}
            </div>
            
            {/* Milestone Cards - Scrollable */}
            <div className="relative">
              <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {(Array.isArray(timelineContent) ? timelineContent : timeline).map((item, index) => {
                  const timelineData = Array.isArray(timelineContent) ? timelineContent : timeline;
                  const isFoundingMoment = index === 0;
                  const isHeadOffice = item.title?.includes('Gurgaon');
                  const isLatest = index === timelineData.length - 1;
                  
                  // Icon mapping for timeline items (CMS may not have icons, so use defaults)
                  const iconMap = { 
                    Building, Sparkles, Star, Calendar,
                    'building': Building,
                    'sparkles': Sparkles,
                    'star': Star,
                    'calendar': Calendar
                  };
                  // Determine icon: use component directly if it exists, else look up in map, else use defaults based on position
                  const IconComponent = item.icon && (typeof item.icon === 'function' || typeof item.icon === 'object')
                    ? item.icon 
                    : (typeof item.icon === 'string' ? iconMap[item.icon] || iconMap[item.icon.toLowerCase()] : null) 
                      || (isFoundingMoment ? Sparkles : isHeadOffice ? Star : Building);
                  
                  return (
                    <div key={index} className="flex-shrink-0 w-72 snap-center group">
                      <div className={`h-full p-5 rounded-2xl border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                        isFoundingMoment 
                          ? 'bg-gradient-to-br from-ux-amber/10 to-ux-coral/10 border-ux-amber/30' 
                          : isHeadOffice
                          ? 'bg-gradient-to-br from-ux-cyan/10 to-ux-blue/10 border-ux-cyan/30'
                          : isLatest
                          ? 'bg-gradient-to-br from-ux-green/10 to-ux-cyan/10 border-ux-green/30'
                          : isDark 
                          ? 'bg-[#1a2830] border-white/10 hover:border-white/20' 
                          : 'bg-white border-ux-charcoal/10 hover:border-ux-charcoal/20'
                      }`}>
                        <div className="flex items-center justify-between mb-3">
                          <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${
                            isFoundingMoment ? 'bg-ux-amber/20 text-ux-amber' 
                            : isHeadOffice ? 'bg-ux-cyan/20 text-ux-cyan'
                            : isLatest ? 'bg-ux-green/20 text-ux-green'
                            : isDark ? 'bg-white/10 text-white/80' : 'bg-ux-charcoal/10 text-ux-charcoal/80'
                          }`}>
                            {item.year}
                          </span>
                          <span className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>{item.date}</span>
                        </div>
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                          isFoundingMoment ? 'bg-ux-amber/20' 
                          : isHeadOffice ? 'bg-ux-cyan/20'
                          : isLatest ? 'bg-ux-green/20'
                          : isDark ? 'bg-white/5' : 'bg-ux-charcoal/5'
                        }`}>
                          <IconComponent className={`w-5 h-5 ${
                            isFoundingMoment ? 'text-ux-amber' 
                            : isHeadOffice ? 'text-ux-cyan'
                            : isLatest ? 'text-ux-green'
                            : isDark ? 'text-white/60' : 'text-ux-charcoal/60'
                          }`} />
                        </div>
                        <h4 className={`font-bold text-base mb-1.5 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{item.title}</h4>
                        <p className={`text-sm ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>{item.description}</p>
                        {(isFoundingMoment || isHeadOffice || isLatest) && (
                          <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-ux-charcoal/10'}`}>
                            <span className={`text-[10px] font-semibold uppercase tracking-wider ${
                              isFoundingMoment ? 'text-ux-amber' : isHeadOffice ? 'text-ux-cyan' : 'text-ux-green'
                            }`}>
                              {isFoundingMoment ? 'Founded' : isHeadOffice ? 'HQ' : 'Latest'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-center gap-2 mt-4">
                <span className={`text-xs ${isDark ? 'text-white/40' : 'text-ux-charcoal/40'}`}>← Scroll to explore →</span>
              </div>
            </div>
            
            {/* Summary Stats */}
            <div className={`mt-12 p-6 rounded-2xl ${isDark ? 'bg-[#1a2830]/80' : 'bg-white/80'} backdrop-blur-sm border ${isDark ? 'border-white/10' : 'border-ux-charcoal/10'}`}>
              <div className="flex flex-wrap justify-center gap-8 md:gap-16">
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-ux-amber">2020</div>
                  <div className={`text-xs ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Founded</div>
                </div>
                <div className={`hidden md:block w-px ${isDark ? 'bg-white/10' : 'bg-ux-charcoal/10'}`} />
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold gradient-text-electric">11</div>
                  <div className={`text-xs ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Offices</div>
                </div>
                <div className={`hidden md:block w-px ${isDark ? 'bg-white/10' : 'bg-ux-charcoal/10'}`} />
                <div className="text-center">
                  <div className={`text-2xl md:text-3xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>6</div>
                  <div className={`text-xs ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>States</div>
                </div>
                <div className={`hidden md:block w-px ${isDark ? 'bg-white/10' : 'bg-ux-charcoal/10'}`} />
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-ux-green">5</div>
                  <div className={`text-xs ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Years</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Senior Leadership Section - Dynamic from CMS */}
      <section className={`py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className={`mb-4 ${isDark ? 'bg-ux-cyan/20 text-ux-cyan' : 'bg-ux-cyan/10 text-ux-cyan'}`}>
              {leadershipHeader.badge}
            </Badge>
            <h2 className={`text-3xl md:text-5xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-ux-charcoal'}`} data-testid="leadership-header-title">
              {leadershipHeader.title}
              <span className="gradient-text-aurora"> {leadershipHeader.title_highlight}</span>
            </h2>
            <p className={`mt-4 text-lg max-w-3xl mx-auto ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
              {leadershipHeader.subtitle}
            </p>
          </div>
          
          {teamLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-ux-cyan"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member, index) => (
                <div 
                  key={member.id || index}
                  className="group text-center flex flex-col"
                >
                  {/* Photo with solid background - clean circular style */}
                  <div className={`relative w-48 h-48 mx-auto mb-6 rounded-full overflow-hidden ${isDark ? 'bg-slate-600' : 'bg-slate-100'}`}>
                    <img 
                      src={member.image_url}
                      alt={member.name}
                      className="w-full h-full object-cover object-top transition-all duration-500 grayscale group-hover:grayscale-0"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=0D8ABC&color=fff&size=200`;
                      }}
                    />
                    {/* LinkedIn icon - appears on hover */}
                    {member.linkedin_url && (
                      <a 
                        href={member.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Linkedin className="w-8 h-8 text-white" />
                      </a>
                    )}
                  </div>
                  
                  {/* Name and Role - fixed height container for alignment */}
                  <h3 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{member.name}</h3>
                  <p className={`text-sm font-medium mb-3 min-h-[40px] flex items-center justify-center ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`}>{member.role}</p>
                  
                  {/* Department badge */}
                  <div className="mb-3">
                    <Badge variant="outline" className={`${isDark ? 'border-ux-cyan/30 text-ux-cyan' : 'border-ux-blue/30 text-ux-blue'}`}>
                      {member.department}
                    </Badge>
                  </div>
                  
                  {/* Bio - fixed min height for alignment */}
                  <p className={`text-sm mt-auto min-h-[40px] ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                    {member.bio || ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Values Section */}
      <section className={`py-24 ${isDark ? 'bg-[#233842]' : 'bg-ux-slate-light'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className={`mb-4 ${isDark ? 'bg-ux-amber/20 text-ux-amber' : 'bg-ux-amber/10 text-ux-amber'}`}>
              {valuesHeader.badge}
            </Badge>
            <h2 className={`text-3xl md:text-5xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-ux-charcoal'}`} data-testid="values-header-title">
              {valuesHeader.title}
              <span className="gradient-text-sunset"> {valuesHeader.title_highlight}</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(Array.isArray(valuesContent) ? valuesContent : values).map((value, index) => {
              // Icon mapping for CMS values (supports both PascalCase and lowercase)
              const iconMap = { 
                Target, Award, Heart, Eye,
                'target': Target, 
                'award': Award, 
                'heart': Heart, 
                'eye': Eye
              };
              // Determine icon: use the component reference directly if it exists, else look up in map
              const IconComponent = value.icon && (typeof value.icon === 'function' || typeof value.icon === 'object')
                ? value.icon 
                : (typeof value.icon === 'string' ? iconMap[value.icon] || iconMap[value.icon.toLowerCase()] : null) || Target;
              return (
              <div 
                key={index}
                className="glass-panel rounded-3xl p-8 group hover:scale-105 transition-transform"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colorClasses[value.color] || colorClasses.cyan} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <IconComponent className="w-7 h-7 text-white" />
                </div>
                <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  {value.title}
                </h3>
                <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                  {value.description}
                </p>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Life at UniXperts Section */}
      <section className={`py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className={`mb-4 ${isDark ? 'bg-ux-rose/20 text-ux-rose' : 'bg-ux-rose/10 text-ux-rose'}`}>
              {cultureHeader.badge}
            </Badge>
            <h2 className={`text-3xl md:text-5xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-ux-charcoal'}`} data-testid="culture-header-title">
              {cultureHeader.title}
              <span className="gradient-text-electric"> {cultureHeader.title_highlight}</span>
            </h2>
            <p className={`mt-4 text-lg max-w-3xl mx-auto ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
              {cultureHeader.subtitle}
            </p>
          </div>
          
          {/* Photo Grid - Masonry style with 14 photos */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
            {lifeAtUnixperts.map((item, index) => {
              // Create varied layout: first image is large, some are tall, rest are standard
              const isLarge = index === 0;
              const isTall = index === 3 || index === 7 || index === 11;
              const isWide = index === 5 || index === 9;
              
              return (
                <div 
                  key={item.key}
                  className={`relative rounded-2xl overflow-hidden group cursor-pointer ${
                    isLarge ? 'col-span-2 row-span-2' : 
                    isTall ? 'row-span-2' : 
                    isWide ? 'col-span-2' : ''
                  }`}
                >
                  <img 
                    src={item.image}
                    alt={item.title}
                    className={`w-full object-cover group-hover:scale-110 transition-transform duration-500 ${
                      isLarge ? 'h-64 md:h-full min-h-[320px]' : 
                      isTall ? 'h-48 md:h-full min-h-[280px]' : 
                      'h-40 md:h-48'
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h4 className="text-white font-bold text-sm md:text-base">{item.title}</h4>
                    <p className="text-white/80 text-xs md:text-sm hidden md:block">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* GPTW Banner */}
          <div className={`mt-16 p-8 rounded-3xl ${isDark ? 'bg-[#233842]' : 'bg-ux-slate-light'} flex flex-col md:flex-row items-center justify-between gap-8`}>
            <div className="flex items-center gap-6">
              <img src={isDark ? gptwBadgeDark : gptwBadgeLight} alt="Great Place to Work" className="h-24 w-auto" />
              <div>
                <h3 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`} data-testid="gptw-banner-title">
                  {gptwBanner.title}
                </h3>
                <p className={`${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                  {gptwBanner.subtitle}
                </p>
              </div>
            </div>
            <Button className="bg-gradient-to-r from-ux-violet to-ux-rose text-white rounded-full whitespace-nowrap" asChild>
              <a
                href="mailto:hr@unixperts.com?subject=Interested%20in%20joining%20UniXperts&body=Hi%20UniXperts%20HR%2C%0A%0AI%27m%20interested%20in%20joining%20your%20team.%20Please%20find%20my%20details%20below%3A%0A%0AName%3A%0ARole%20interested%20in%3A%0APhone%3A%0A%0AThanks%21"
                data-testid="join-our-team-btn"
              >
                {gptwBanner.cta_text || 'Join Our Team'} <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Careers CTA Section */}
      <section className={`py-16 ${isDark ? 'bg-gradient-to-r from-[#233842] to-[#1a2830]' : 'bg-gradient-to-r from-ux-blue to-ux-violet'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="careers-cta-title">
              {careersCta.title}
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              {careersCta.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                className="bg-white text-ux-violet hover:bg-white/90 rounded-full px-8"
                asChild
              >
                <a href={careersCta.cta_primary_link || 'mailto:hr@unixperts.com'}>
                  <Mail className="w-4 h-4 mr-2" />
                  {careersCta.cta_primary_text}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Office Section */}
      <section className={`py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className={`mb-4 ${isDark ? 'bg-ux-green/20 text-ux-green' : 'bg-ux-green/10 text-ux-green'}`}>
              Visit Us
            </Badge>
            <h2 className={`text-3xl md:text-5xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
              Our
              <span className="gradient-text-electric"> Offices</span>
            </h2>
            <p className={`mt-4 text-lg ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
              Spread across 10 major cities in India
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {officesContent.map((office, index) => (
              <Card key={index} className={`overflow-hidden ${isDark ? 'bg-[#233842] border-white/10' : ''}`}>
                <div className="relative h-48 bg-gradient-to-br from-ux-blue via-ux-violet to-ux-cyan">
                  {office.image && (
                    <img 
                      src={office.image}
                      alt={office.city}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white text-xs font-semibold uppercase tracking-wide">
                      <Building className="w-3 h-3" /> Corporate Office
                    </span>
                  </div>
                  <h3 className="absolute bottom-4 left-4 right-4 text-xl md:text-2xl font-bold text-white font-display drop-shadow-md">
                    {office.city || 'Gurugram (Head Office)'}
                  </h3>
                </div>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className={`w-5 h-5 mt-0.5 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
                    <p className={`${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>{office.address}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className={`w-5 h-5 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
                    <a href={`tel:${office.phone}`} className={`${isDark ? 'text-white/70 hover:text-white' : 'text-ux-charcoal/70 hover:text-ux-charcoal'}`}>
                      {office.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className={`w-5 h-5 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
                    <a href={`mailto:${office.email}`} className={`${isDark ? 'text-white/70 hover:text-white' : 'text-ux-charcoal/70 hover:text-ux-charcoal'}`}>
                      {office.email}
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* Map or illustration placeholder */}
            <div className={`rounded-3xl overflow-hidden ${isDark ? 'bg-[#233842]' : 'bg-ux-slate-light'} p-8 flex flex-col items-center justify-center h-full min-h-[300px]`}>
              <Globe className={`w-16 h-16 mb-4 ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
              <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                11 Offices Across 10 Cities
              </h3>
              <p className={`text-center ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                Gurgaon • Ahmedabad • Pune • Hyderabad • Bengaluru • Chennai • Kochi • Kottayam • Vijayawada • Visakhapatnam
              </p>
              <Button className="mt-6 rounded-full" variant={isDark ? 'hero-outline' : 'outline'} asChild>
                <Link to="/contact">Find Your Nearest Office</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA with GPTW */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-ux-violet via-ux-blue to-ux-cyan" />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-xl p-3 shadow-2xl">
              <img src={gptwBadgeHero} alt="Great Place to Work" className="h-24 w-auto" />
            </div>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Join 30,000+ students who trusted UniXperts to guide them to their dream university abroad.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-ux-violet hover:bg-white/90 rounded-full px-8"
              asChild
            >
              <Link to="/contact">
                Book Free Consultation <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button 
              size="lg"
              variant="hero-outline"
              className="rounded-full px-8"
              asChild
            >
              <Link to="/contact">Book Consultation</Link>
            </Button>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default AboutPage;
